import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@/components/atoms/Button'
import { Input } from '@/components/atoms/Input'
import { MortiseMark } from '@/components/atoms/MortiseMark'

export default function ForgotPasswordPage() {
  useEffect(() => {
    document.title = "Tenon — Forgot Password"
    return () => { document.title = "Tenon" }
  }, [])

  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'sent' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setStatus('loading')
    setErrorMsg('')
    try {
      const res = await fetch('/api/v1/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      })
      if (!res.ok) {
        const j = await res.json().catch(() => ({}))
        throw new Error((j as any).message || 'Something went wrong')
      }
      setStatus('sent')
    } catch (err: unknown) {
      setStatus('error')
      setErrorMsg(err instanceof Error ? err.message : 'Something went wrong')
    }
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'var(--bg-page)',
      fontFamily: "'Karla', -apple-system, sans-serif",
      paddingTop: 'calc(24px + env(safe-area-inset-top, 0px))',
      paddingBottom: 'calc(24px + env(safe-area-inset-bottom, 0px))',
      paddingLeft: '16px',
      paddingRight: '16px',
      position: 'relative',
    }}>
      {/* Gold gradient ambient */}
      <div style={{
        position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 0,
        background: 'radial-gradient(80% 60% at 100% 0%, color-mix(in oklch, var(--brand-gold) 10%, transparent), transparent 70%)',
      }} />

      <div style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', zIndex: 1 }}>

        <a href="https://usetenon.com" style={{ display: 'flex', alignItems: 'center', marginBottom: 32, textDecoration: 'none' }}>
          <MortiseMark size={44} light />
        </a>

        <div style={{
          width: '100%',
          background: 'var(--bg-surface)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          padding: '36px 40px',
          boxShadow: 'var(--shadow-2)',
        }}>
          {/* Eyebrow */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 10 }}>
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--brand-gold)', display: 'inline-block', flexShrink: 0 }} />
            <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: 'var(--text-muted)', fontFamily: "'Karla', -apple-system, sans-serif" }}>
              Tenon · Password Reset
            </span>
          </div>

          <h1 style={{ fontSize: '1.625rem', fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 6px', letterSpacing: '-0.025em', lineHeight: 1.1 }}>
            Reset your password
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-secondary)', marginBottom: 28, marginTop: 0, lineHeight: 1.55 }}>
            Enter your email and we'll send you a reset link.
          </p>

          {status === 'sent' ? (
            <div style={{ textAlign: 'center' }}>
              <div style={{
                padding: '16px',
                background: 'color-mix(in oklch, var(--st-active) 8%, transparent)',
                border: '1px solid color-mix(in oklch, var(--st-active) 40%, transparent)',
                borderRadius: 10,
                color: 'var(--st-active)',
                marginBottom: 24,
                fontSize: 14,
                lineHeight: 1.6,
              }}>
                ✓ Check your inbox — a reset link is on its way.
              </div>
              <Link to="/login" style={{ color: 'var(--text-secondary)', fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>
                ← Back to log in
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
              {status === 'error' && (
                <div style={{
                  padding: '10px 14px',
                  background: 'color-mix(in oklch, var(--st-error) 8%, transparent)',
                  border: '1px solid color-mix(in oklch, var(--st-error) 40%, transparent)',
                  borderRadius: 8,
                  color: 'var(--st-error)',
                  fontSize: 13,
                  marginBottom: 16,
                }}>
                  {errorMsg}
                </div>
              )}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 24 }}>
                <label htmlFor="email" style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>Email</label>
                <Input
                  type="email"
                  id="email"
                  placeholder="you@yourcompany.com"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                disabled={status === 'loading'}
                style={{ width: '100%', marginBottom: 20 }}
              >
                {status === 'loading' ? 'Sending…' : 'Send reset link'}
              </Button>
              <div style={{ textAlign: 'center' }}>
                <Link to="/login" style={{ color: 'var(--text-secondary)', fontSize: 13, textDecoration: 'none', fontWeight: 600 }}>
                  ← Back to log in
                </Link>
              </div>
            </form>
          )}
        </div>

        {/* Sage callout */}
        <div style={{
          marginTop: 24,
          background: 'color-mix(in oklch, var(--brand-gold) 7%, var(--bg-surface))',
          border: '1px solid color-mix(in oklch, var(--brand-gold) 18%, var(--border-subtle))',
          borderRadius: 10,
          padding: '14px 18px',
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
          width: '100%',
        }}>
          <div style={{
            width: 30, height: 30, borderRadius: 7, flexShrink: 0,
            background: 'color-mix(in oklch, var(--brand-gold) 15%, var(--bg-elevated))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 800, fontSize: 12, color: 'var(--brand-gold)',
            fontFamily: "'Karla', -apple-system, sans-serif",
          }}>S</div>
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.55, margin: 0 }}>
            <strong style={{ color: 'var(--text-primary)' }}>Sage is watching your projects.</strong> Log in to see what needs your attention today.
          </p>
        </div>

        <div style={{ marginTop: 20 }}>
          <Link to="/login" style={{ color: 'var(--text-muted)', fontSize: 12, textDecoration: 'none' }}>
            ← Back to usetenon.com
          </Link>
        </div>
      </div>
    </div>
  )
}
